import React, { useRef } from "react";
import Select from "react-select";
import _ from "lodash";
import { USER_TYPES } from "../../Constants";
import { getEntity, updateEntity, deleteEntity, addEntity } from "../../API";
import UpdateDeleteBar from "../../components/UpdateDeleteBar";
import {
  Form,
  useLoaderData,
  redirect,
  useSubmit,
  useActionData,
} from "react-router-dom";
import {
  assert,
  object,
  string,
  nonempty,
  StructError,
  optional,
} from "superstruct";

const userSchema = object({
  id: optional(string()),
  firstname: nonempty(string()),
  lastname: nonempty(string()),
  username: nonempty(string()),
  password: nonempty(string()),
  type: nonempty(string()),
});

export async function loader({ params }) {
  if (params.userId) return getEntity("users", params.userId);
  return { firstname: "", lastname: "", username: "", password: "", type: "" };
}

export async function userAction({ request, params }) {
  const formData = await request.formData();

  let user = Object.fromEntries(formData);

  if (params.userId) user.id = params.userId;

  switch (request.method) {
    case "PUT": {
      const errors = hasErrors(user);
      if (errors) return errors;
      await updateEntity("users", user);
      break;
    }
    case "POST": {
      const errors = hasErrors(user);
      if (errors) return errors;
      await addEntity("users", user);
      break;
    }
    case "DELETE": {
      await deleteEntity("users", user);
      break;
    }
    default: {
      throw new Response("", { status: 405 });
    }
  }

  return redirect(`../users`);
}

function hasErrors(user) {
  try {
    assert(user, userSchema);
  } catch (err) {
    if (err instanceof StructError) {
      const fieldsErrors = err.failures().reduce(
        (acc, { key, message }) => ({
          ...acc,
          [key]: message,
        }),
        {}
      );
      return fieldsErrors;
    }
  }
  return null;
}

function Field({ label, name, type = "text", defaultValue, error }) {
  return (
    <>
      <label className="form-label fw-medium mb-1">{label}</label>
      {error && <div className="text-danger small mb-1">{error}</div>}
      <input
        type={type}
        className={`form-control${error ? " is-invalid" : ""}`}
        name={name}
        defaultValue={defaultValue}
      />
    </>
  );
}

function User() {
  const user = useLoaderData();
  const errors = useActionData();
  const ref = useRef();
  const submit = useSubmit();

  const title = user.firstname && user.lastname
    ? `${user.firstname} ${user.lastname}`
    : "New User";

  const itemDelete = () => submit(ref.current, { method: "delete" });
  const itemSave = () => submit(ref.current, { method: user.id ? "put" : "post" });

  return (
    <div className="flex-grow-1 overflow-auto">
      <UpdateDeleteBar onDelete={itemDelete} onSave={itemSave} title={title} />

      <div className="container-fluid py-4 px-4">
        {errors && Object.keys(errors).length > 0 && (
          <div className="alert alert-danger mb-3">
            Please fix the following fields: {Object.keys(errors).join(", ")}
          </div>
        )}

        <Form ref={ref} id="user-form">
          <div className="card shadow-sm mb-4">
            <div className="card-body p-4">
              <div className="row g-3">
                <div className="col-md-6">
                  <Field label="First Name" name="firstname" defaultValue={user.firstname} error={errors?.firstname} />
                </div>
                <div className="col-md-6">
                  <Field label="Last Name" name="lastname" defaultValue={user.lastname} error={errors?.lastname} />
                </div>
                <div className="col-md-4">
                  <Field label="Username" name="username" defaultValue={user.username} error={errors?.username} />
                </div>
                <div className="col-md-4">
                  <Field label="Password" name="password" type="password" defaultValue={user.password} error={errors?.password} />
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-medium mb-1">Type</label>
                  {errors?.type && <div className="text-danger small mb-1">{errors.type}</div>}
                  <Select
                    name="type"
                    defaultValue={{ name: user.type }}
                    getOptionLabel={(t) => t.name}
                    getOptionValue={(t) => t.name}
                    options={_.filter(USER_TYPES, (t) => t.name.toLowerCase() !== (user.type || "").toLowerCase())}
                    isMulti={false}
                  />
                </div>
              </div>
            </div>
          </div>
        </Form>
      </div>
    </div>
  );
}

export default User;
